import type {
  RequisitionsParams,
  RequisitionsResponse,
} from '@/pages/requisitions/associate-req/associateReq.interface';
import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
} from '@tanstack/react-query';
import { http } from '@/lib/ats-https';

export const ASSOCIATE_REQUISITIONS_API = {
  // Get all requisitions with filtering and sorting
  getRequisitions: async (
    params: RequisitionsParams = {}
  ): Promise<RequisitionsResponse> => {
    const defaultParams: RequisitionsParams = {
      sort_key: 'created_at',
      sort_order: 'desc',
      page: 1,
      limit: 200,
      ...params,
    };

    const response = await http.get('/requisitions', { params: defaultParams });
    return response.data;
  },

  deleteRequisition: async (
    atsApplicationId: string,
    stageId?: number | string
  ): Promise<void> => {
    const data = stageId ? { to_stage_id: stageId } : {};
    const response = await http.delete(
      `/ats_applications/${atsApplicationId}/remove_requisition `,
      { data }
    );
    return response.data;
  },

  addRequisitionsToJob: async (
    jobId: string | number,
    requisitionCodes: string[]
  ): Promise<any> => {
    const response = await http.post(`/ats_jobs/${jobId}/add_requisitions`, {
      requisition_codes: requisitionCodes,
    });
    return response.data;
  },

  removeRequisitionsFromJob: async (
    jobId: string | number,
    requisitionCodes: string[]
  ): Promise<any> => {
    const response = await http.delete(
      `/ats_jobs/${jobId}/remove_requisitions`,
      {
        data: {
          requisition_codes: requisitionCodes,
        },
      }
    );
    return response;
  },
};

export const associateRequisitionsKeys = {
  all: ['associate-requisitions'] as const,
  lists: () => [...associateRequisitionsKeys.all, 'list'] as const,
  list: (filters: RequisitionsParams) =>
    [...associateRequisitionsKeys.lists(), filters] as const,
  details: () => [...associateRequisitionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...associateRequisitionsKeys.details(), id] as const,
};

export const useAssociateRequisitions = <TData = RequisitionsResponse>(
  params: RequisitionsParams = {},
  options?: Omit<
    UseQueryOptions<RequisitionsResponse, Error, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: associateRequisitionsKeys.list(params),
    queryFn: () => ASSOCIATE_REQUISITIONS_API.getRequisitions(params),
    staleTime: 0,
    ...options,
  });
};

export const useDeleteRequisition = (atsApplicationId: string) => {
  return useMutation({
    mutationFn: (stageId?: number | string) =>
      ASSOCIATE_REQUISITIONS_API.deleteRequisition(atsApplicationId, stageId),
  });
};

export const useAddRequisitionsToJob = () => {
  return useMutation({
    mutationFn: ({
      jobId,
      requisitionCodes,
    }: {
      jobId: string | number;
      requisitionCodes: string[];
    }) =>
      ASSOCIATE_REQUISITIONS_API.addRequisitionsToJob(jobId, requisitionCodes),
  });
};

export const useRemoveRequisitionsFromJob = () => {
  return useMutation({
    mutationFn: ({
      jobId,
      requisitionCodes,
    }: {
      jobId: string | number;
      requisitionCodes: string[];
    }) =>
      ASSOCIATE_REQUISITIONS_API.removeRequisitionsFromJob(
        jobId,
        requisitionCodes
      ),
  });
};

export default ASSOCIATE_REQUISITIONS_API;
